import React, { useState, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";
import { useNavigate } from "react-router-dom";
import { app } from "../firebase"; 
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  getDocs,
  collection,
  query,
  where
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useBarbeiro } from "../context/BarbeiroContext"; // <--- IMPORTANTE

import {
  LuTrash2,
  LuPlus,
  LuPencil,
  LuCheck,
  LuX,
  LuUser,
  LuMail,
  LuLock,
  LuLoaderCircle,
  LuUsers,
  LuShieldCheck,
  LuChevronRight
} from "react-icons/lu";

// --- ANIMAÇÕES ---
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// --- STYLED COMPONENTS ---

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #3b82f6;
  background-image: radial-gradient(#76a9fc 1px, transparent 1px);
  background-size: 20px 20px;
  padding: 1rem;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  width: 100%;
  max-width: 700px;
  border-radius: 24px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  padding: 1.5rem;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;

  @media (min-width: 640px) {
    padding: 2.5rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  flex-shrink: 0;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;

  svg { color: #2563eb; }
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 1rem;
`;

const ScrollArea = styled.div`
  overflow-y: auto;
  padding-right: 0.5rem;
  margin-bottom: 1rem;
  flex-grow: 1;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
  &::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 10px; }
`;

const BarberItem = styled.div`
  background: white;
  border: 1px solid ${props => props.$editing ? '#3b82f6' : '#e5e7eb'};
  border-radius: 16px;
  padding: 1rem;
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  transition: all 0.2s;
  animation: ${fadeIn} 0.3s ease-out;
  box-shadow: ${props => props.$editing ? '0 0 0 2px rgba(59, 130, 246, 0.1)' : '0 1px 3px rgba(0,0,0,0.05)'};
  position: relative;

  ${props => props.$isOwner && css`
    background-color: #f8fafc;
    border-color: #e2e8f0;
  `}

  @media (min-width: 640px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const OwnerBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: #dbeafe;
  color: #1e40af;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.25rem 0.5rem;
  border-radius: 99px;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const AvatarUploader = styled.label`
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  border-radius: 50%;
  background-color: #f3f4f6;
  border: 2px dashed ${props => props.$hasImage ? 'transparent' : '#d1d5db'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.$disabled ? 'default' : 'pointer'};
  overflow: hidden;
  transition: all 0.2s;
  position: relative;

  &:hover {
    border-color: ${props => props.$disabled ? '#d1d5db' : '#3b82f6'};
    background-color: ${props => props.$disabled ? '#f3f4f6' : '#eff6ff'};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
`;

const InputGroup = styled.div`
  position: relative;
  width: 100%;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
  display: flex;
  align-items: center;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  padding-left: 2.75rem;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #111827;
  font-size: 0.95rem;
  outline: none;
  transition: all 0.2s;

  &:focus {
    background: white;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const BarberInfo = styled.div`
  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 0.25rem;
  }
  
  p {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #4b5563;
    font-size: 0.9rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: auto;
  justify-content: flex-end;
  width: 100%;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s;

  ${props => props.$variant === 'primary' && css`
    background-color: #eff6ff;
    color: #2563eb;
    &:hover { background-color: #dbeafe; }
  `}

  ${props => props.$variant === 'danger' && css`
    background-color: #fef2f2;
    color: #ef4444;
    &:hover { background-color: #fee2e2; }
  `}

  ${props => props.$variant === 'success' && css`
    background-color: #2563eb;
    color: white;
    &:hover { background-color: #1d4ed8; }
  `}

  ${props => props.$variant === 'ghost' && css`
    background-color: transparent;
    color: #6b7280;
    border: 1px solid #e5e7eb;
    &:hover { background-color: #f3f4f6; }
  `}
`;

const AddButtonCard = styled.button`
  width: 100%;
  border: 2px dashed #d1d5db;
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: #f9fafb;
  color: #6b7280;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 0.5rem;

  &:hover {
    border-color: #3b82f6;
    color: #3b82f6;
    background: #eff6ff;
  }
`;

const Footer = styled.div`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #f3f4f6;
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
`;

const MainButton = styled.button`
  width: 100%;
  padding: 0.875rem;
  border-radius: 12px;
  border: none;
  background-color: ${props => props.disabled ? '#93c5fd' : '#2563eb'};
  color: white;
  font-weight: 600;
  font-size: 1rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s;
  box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2);

  &:hover {
    background-color: ${props => props.disabled ? '#93c5fd' : '#1d4ed8'};
    transform: ${props => props.disabled ? 'none' : 'scale(0.99)'};
  }
`;

const SpinnerIcon = styled(LuLoaderCircle)`
  animation: ${spin} 1s linear infinite;
`;

// --- COMPONENT MAIN ---

export default function CadastroBarbeiros() {
  const navigate = useNavigate();
  const auth = getAuth(app);
  const db = getFirestore(app);
  const storage = getStorage(app);
  
  // Pegamos a função de recarregar do contexto
  const { recarregarPerfil } = useBarbeiro();

  const [barbeiros, setBarbeiros] = useState([]);
  const [isSalvando, setIsSalvando] = useState(false);
  const [subdominio, setSubdominio] = useState(null);
  const [loadingInit, setLoadingInit] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/login");
        return;
      }

      try {
        // Descobre subdomínio do criador
        const queryCriador = query(
          collection(db, "barbearia"),
          where("criadorUid", "==", user.uid)
        );
        const snapCriador = await getDocs(queryCriador);

        if (snapCriador.empty) {
          navigate("/cadastro-barbearia");
          return;
        }

        const foundSubdominio = snapCriador.docs[0].id;
        setSubdominio(foundSubdominio);

        // Lê lead e doc do barbeiro proprietário
        const leadRef = doc(db, "leads", user.uid);
        const leadSnap = await getDoc(leadRef);

        const barbeiroRef = doc(db, `barbearia/${foundSubdominio}/barbeiros`, user.uid);
        const barbeiroSnap = await getDoc(barbeiroRef);

        if (leadSnap.exists() && barbeiroSnap.exists()) {
          const leadData = leadSnap.data();
          const barbeiroData = barbeiroSnap.data();

          // Proprietário em modo SOMENTE LEITURA
          setBarbeiros(prev => ([
            {
              idTemp: 'owner',
              nome: barbeiroData.nome || leadData.nome || "",
              email: barbeiroData.email || leadData.email || "",
              telefone: leadData.telefone || "",
              file: null,
              preview: barbeiroData.foto ? barbeiroData.foto : null,
              uid: user.uid,
              proprietario: true,
              editando: false, 
            },
            ...prev
          ]));
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingInit(false);
      }
    });

    return () => unsubscribe();
  }, [auth, db, navigate]);

  // ---- HELPERS ----

  const adicionarBarbeiro = () => {
    setBarbeiros(prev => ([
      ...prev,
      { 
        idTemp: Date.now(), 
        nome: "", 
        email: "", 
        senha: "", 
        file: null, 
        preview: null, 
        proprietario: false, 
        editando: true 
      }
    ]));
  };

  const removerBarbeiro = (index) => {
    if (barbeiros[index]?.proprietario) return; 
    setBarbeiros(prev => prev.filter((_, i) => i !== index));
  };

  const handleChange = (index, field, value) => {
    if (barbeiros[index]?.proprietario) return;
    const novos = [...barbeiros];
    novos[index][field] = value;
    setBarbeiros(novos);
  };

  const handleImageChange = (index, file) => {
    if (barbeiros[index]?.proprietario) return;
    const novos = [...barbeiros];
    novos[index].file = file;
    novos[index].preview = file ? URL.createObjectURL(file) : null;
    setBarbeiros(novos);
  };

  const confirmarEdicao = (index) => {
    const b = barbeiros[index];
    if (b.proprietario) return;

    if (!b.nome || !b.email || !b.senha) {
      alert("Preencha Nome, Email e Senha.");
      return;
    }
    const novos = [...barbeiros];
    novos[index].editando = false;
    setBarbeiros(novos);
  };

  const editar = (index) => {
    if (barbeiros[index]?.proprietario) return; 
    const novos = [...barbeiros];
    novos[index].editando = true;
    setBarbeiros(novos);
  };

  const cancelar = (index) => {
    const b = barbeiros[index];
    if (b.proprietario) return;

    const isVazio = !b.nome && !b.email && !b.senha && !b.file && !b.preview;
    if (isVazio) {
      removerBarbeiro(index);
    } else {
      const novos = [...barbeiros];
      novos[index].editando = false;
      setBarbeiros(novos);
    }
  };

  // Cria usuário sem derrubar a sessão do admin
  const criarUsuarioSemDeslogar = async (email, senha) => {
    const REAL_API_KEY = app.options.apiKey; 

    const response = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${REAL_API_KEY}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password: senha, returnSecureToken: false })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error?.message || "Erro ao criar usuário.");
    return data.localId; // UID
  };

  const salvarTodos = async () => {
    if (!subdominio) { alert("Subdomínio não carregado."); return; }
    if (isSalvando) return;

    const emEdicao = barbeiros.some(b => b.editando && !b.proprietario);
    if (emEdicao) { alert("Finalize a edição dos cards antes de salvar."); return; }

    const camposInvalidos = barbeiros
      .filter(b => !b.proprietario)
      .some(b => !b.nome || !b.email || !b.senha);
    if (camposInvalidos) { alert("Preencha todos os campos obrigatórios."); return; }

    setIsSalvando(true);
    try {
      const servicosSnap = await getDocs(collection(db, `barbearia/${subdominio}/servico`));
      const todosServicosIds = servicosSnap.docs.map(doc => doc.id);

      for (const b of barbeiros) {
        if (b.proprietario) continue;

        try {
          const uid = await criarUsuarioSemDeslogar(b.email, b.senha);
          let url = "";

          if (b.file) {
            const fotoRef = ref(storage, `${subdominio}/barbeiros/${uid}.jpg`);
            await uploadBytes(fotoRef, b.file);
            url = await getDownloadURL(fotoRef);
          }

          await setDoc(doc(db, `barbearia/${subdominio}/barbeiros`, uid), {
            nome: b.nome,
            email: b.email,
            foto: url,
            uid,
            cargo: "barbeiro",
            barbeariaId: subdominio, // Garantia extra
            servicosIds: todosServicosIds,
          });

        } catch (err) {
          console.error("Erro ao criar barbeiro:", err);
          let msg = err.message;
          if (msg.includes("EMAIL_EXISTS")) msg = `O email ${b.email} já está em uso.`;
          else if (msg.includes("WEAK_PASSWORD")) msg = "Senha muito fraca.";
          else if (msg.includes("INVALID_EMAIL")) msg = "Email inválido.";
          
          alert(msg);
          setIsSalvando(false);
          return;
        }
      }

      await setDoc(
        doc(db, "barbearia", subdominio),
        { SituacaoCadastral: { statusBarbeiro: "ok" } },
        { merge: true }
      );

      // --- PONTO CRÍTICO: Atualiza o contexto antes de navegar ---
      if (recarregarPerfil) {
        await recarregarPerfil();
      }

      navigate("/", { replace: true });

    } catch (error) {
      console.error("Erro geral:", error);
      alert("Erro ao salvar dados.");
    } finally {
      setIsSalvando(false);
    }
  };

  if (loadingInit) {
    return (
      <Container>
        <Card style={{ alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
          <SpinnerIcon style={{ fontSize: '2rem', color: '#2563eb' }} />
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <Header>
          <Title><LuUsers /> Equipe</Title>
          <Subtitle>Cadastre seus barbeiros e parceiros.</Subtitle>
        </Header>

        <ScrollArea>
          {barbeiros.map((barbeiro, index) => {
            const isOwner = !!barbeiro.proprietario;
            
            return (
              <BarberItem key={barbeiro.idTemp || index} $editing={barbeiro.editando} $isOwner={isOwner}>
                {isOwner && (
                    <OwnerBadge><LuShieldCheck /> Proprietário</OwnerBadge>
                )}

                {!isOwner && (
                  <input
                    type="file"
                    accept="image/*"
                    id={`foto-${index}`}
                    style={{ display: "none" }}
                    onChange={(e) => handleImageChange(index, e.target.files[0])}
                  />
                )}

                <AvatarUploader
                  htmlFor={isOwner ? undefined : `foto-${index}`}
                  $disabled={isOwner || !barbeiro.editando}
                  $hasImage={!!barbeiro.preview}
                  as={isOwner ? "div" : "label"}
                >
                  {barbeiro.preview ? (
                    <img src={barbeiro.preview} alt="preview" />
                  ) : (
                    <LuUser style={{ color: "#9ca3af", fontSize: "2rem" }} />
                  )}
                  {barbeiro.editando && !barbeiro.preview && !isOwner && (
                      <div style={{position: 'absolute', bottom: 6, fontSize: '0.6rem', color: '#9ca3af'}}>FOTO</div>
                  )}
                </AvatarUploader>

                <ContentArea>
                  {barbeiro.editando && !isOwner ? (
                    <>
                      <InputGroup>
                        <InputIcon><LuUser /></InputIcon>
                        <StyledInput
                          placeholder="Nome completo"
                          value={barbeiro.nome}
                          onChange={(e) => handleChange(index, "nome", e.target.value)}
                        />
                      </InputGroup>

                      <InputGroup>
                        <InputIcon><LuMail /></InputIcon>
                        <StyledInput
                          placeholder="Email de acesso"
                          type="email"
                          value={barbeiro.email}
                          onChange={(e) => handleChange(index, "email", e.target.value)}
                        />
                      </InputGroup>

                      <InputGroup>
                        <InputIcon><LuLock /></InputIcon>
                        <StyledInput
                          placeholder="Senha"
                          type="password"
                          value={barbeiro.senha}
                          onChange={(e) => handleChange(index, "senha", e.target.value)}
                        />
                      </InputGroup>

                      <ActionButtons>
                        <IconButton $variant="ghost" onClick={() => cancelar(index)}>
                          <LuX /> Cancelar
                        </IconButton>
                        <IconButton $variant="success" onClick={() => confirmarEdicao(index)}>
                          <LuCheck /> Confirmar
                        </IconButton>
                      </ActionButtons>
                    </>
                  ) : (
                    <>
                      <BarberInfo>
                        <h3>{barbeiro.nome || "Sem nome"}</h3>
                        <p><LuMail size={14} /> {barbeiro.email || "—"}</p>
                      </BarberInfo>

                      {!isOwner && (
                        <ActionButtons>
                          <IconButton $variant="primary" onClick={() => editar(index)}>
                            <LuPencil /> Editar
                          </IconButton>
                          <IconButton $variant="danger" onClick={() => removerBarbeiro(index)}>
                            <LuTrash2 /> Excluir
                          </IconButton>
                        </ActionButtons>
                      )}
                    </>
                  )}
                </ContentArea>
              </BarberItem>
            );
          })}

          <AddButtonCard onClick={adicionarBarbeiro}>
            <LuPlus size={20} /> Adicionar Novo Barbeiro
          </AddButtonCard>
        </ScrollArea>

        <Footer>
          {barbeiros.length > 0 && (
            <MainButton onClick={salvarTodos} disabled={isSalvando}>
              {isSalvando ? (
                <>
                    <SpinnerIcon /> Salvando...
                </>
              ) : (
                <>
                    Concluir Cadastro <LuChevronRight />
                </>
              )}
            </MainButton>
          )}
        </Footer>
      </Card>
    </Container>
  );
}